import { useLayoutEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { LandingFooter } from '@pages/LandingPage/ui/LandingFooter';

import { Heading, type HeadingProps } from '@shared/ui/typography';

import { staticPageMarkdown } from '../model/content';

interface MarkdownHeadingBlock {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5;
  content: string;
}

interface MarkdownParagraphBlock {
  type: 'paragraph';
  content: string;
}

interface MarkdownListBlock {
  type: 'list';
  items: string[];
}

type MarkdownBlock = MarkdownHeadingBlock | MarkdownParagraphBlock | MarkdownListBlock;

const parseMarkdown = (source: string): MarkdownBlock[] => {
  const chunks = source
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  return chunks.flatMap((chunk): MarkdownBlock[] => {
    const lines = chunk.split('\n').map((line) => line.trim());

    if (lines.every((line) => line.startsWith('- '))) {
      return [
        {
          type: 'list' as const,
          items: lines.map((line) => line.replace(/^- /, '').trim()),
        },
      ];
    }

    const headingMatch = lines[0]?.match(/^(#{1,5})\s+(.+)$/);

    if (headingMatch) {
      const headingMarker = headingMatch[1];
      const headingContent = headingMatch[2];

      if (!headingMarker || !headingContent) {
        return [{ type: 'paragraph' as const, content: lines.join(' ') }];
      }

      const level = headingMarker.length as MarkdownHeadingBlock['level'];
      const content = headingContent.trim();
      const remainder = lines.slice(1).join(' ').trim();

      return remainder
        ? [
            { type: 'heading' as const, level, content },
            { type: 'paragraph' as const, content: remainder },
          ]
        : [{ type: 'heading' as const, level, content }];
    }

    return [{ type: 'paragraph' as const, content: lines.join(' ') }];
  });
};

const headingPropsByLevel: Record<
  MarkdownHeadingBlock['level'],
  Pick<HeadingProps, 'tag' | 'size' | 'className'>
> = {
  1: { tag: 'h1', size: '5xl', className: 'mb-10 text-center' },
  2: { tag: 'h2', size: '3xl', className: 'mb-4 mt-16' },
  3: { tag: 'h3', size: '2xl', className: 'mb-3 mt-12' },
  4: { tag: 'h4', size: 'xl', className: 'mb-3 mt-12' },
  5: { tag: 'h5', size: 'lg', className: 'mb-2 mt-10' },
};

const getHeadingProps = (level: MarkdownHeadingBlock['level']) => headingPropsByLevel[level];

export const StaticPageExample = () => {
  const { pathname } = useLocation();
  const blocks = useMemo(() => parseMarkdown(staticPageMarkdown), []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  return (
    <div className="min-h-dvh">
      <main
        className="
          px-4 pt-48 pb-32
          sm:px-6
          lg:px-8
        "
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-10">
          <section>
            {blocks.map((block, index) => {
              if (block.type === 'heading') {
                const headingProps = getHeadingProps(block.level);

                return (
                  <Heading key={`${block.content}-${String(index)}`} {...headingProps}>
                    {block.content}
                  </Heading>
                );
              }

              if (block.type === 'list') {
                return (
                  <ul
                    key={`list-${String(index)}`}
                    className="
                      mb-8 list-disc space-y-1 ps-5 text-base/7 text-foreground-4
                      marker:text-foreground-6
                      md:text-lg/7.5
                    "
                  >
                    {block.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              }

              return (
                <p
                  key={`${block.content.slice(0, 24)}-${String(index)}`}
                  className="
                    mt-1 mb-2 text-base/6.5 text-foreground-4
                    md:text-lg/7.5
                  "
                >
                  {block.content}
                </p>
              );
            })}
          </section>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};
